// src/App.js

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ForceUpdateScreen from './components/ForceUpdateScreen';
import { APP_VERSION } from './constants/appVersion';

import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import InvitedRegistration from './components/auth/InvitedRegistration';
const TimeClock = lazy(() => import('./components/employee/TimeClock'));
const Dashboard = lazy(() => import('./components/manager/Dashboard'));

// Protected Route
const ProtectedRoute = ({ children, requiredRole }) => {
  const token = localStorage.getItem('timetracker_token');
  let user = null;

  if (token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      
      // Check token expiration
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < currentTime) {
        // Token expired, clear it and redirect to login
        localStorage.removeItem('timetracker_token');
        return <Navigate to="/login" replace />;
      }
      
      user = payload;
    } catch {
      user = null;
    }
  }

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }
  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowed.includes(user.role)) {
      const isDashboardRole = user.role === 'manager' || user.role === 'reports_viewer';
      return <Navigate to={isDashboardRole ? '/dashboard' : '/time-clock'} replace />;
    }
  }
  return children;
};

// Root redirect: auto-login if token exists
const RootRedirect = () => {
  const token = localStorage.getItem('timetracker_token');
  if (token) {
    let user = null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      
      // Check token expiration
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < currentTime) {
        // Token expired, clear it and redirect to login
        localStorage.removeItem('timetracker_token');
        return <Navigate to="/login" replace />;
      }
      
      user = payload;
    } catch {
      return <Navigate to="/login" replace />;
    }
    const isDashboardRole = user.role === 'manager' || user.role === 'reports_viewer';
    return <Navigate to={isDashboardRole ? '/dashboard' : '/time-clock'} replace />;
  }
  return <Navigate to="/login" replace />;
};

const theme = createTheme({
  palette: {
    primary: { main: '#3f51b5' },
    background: { default: '#f5f5f5' },
  },
});

// Compare semver strings: returns true if current < required
function isOutdated(current, required) {
  const c = current.split('.').map(Number);
  const r = required.split('.').map(Number);
  for (let i = 0; i < Math.max(c.length, r.length); i++) {
    const cv = c[i] || 0;
    const rv = r[i] || 0;
    if (cv < rv) return true;
    if (cv > rv) return false;
  }
  return false;
}

function App() {
  const [updateState, setUpdateState] = useState(null); // null = checking
  const showUpdatePreview = process.env.REACT_APP_PREVIEW_UPDATE === 'true'
    || (process.env.NODE_ENV !== 'production'
      && new URLSearchParams(window.location.search).get('preview') === 'update');

  useEffect(() => {
    const authToken = localStorage.getItem('timetracker_token');
    if (!authToken) return;

    // Silently report app version to backend (native only)
    const platform = window.Capacitor?.getPlatform?.() || 'web';
    if (platform !== 'web') {
      const version = platform === 'ios' ? APP_VERSION.ios : APP_VERSION.android;
      fetch(`https://schooner-backend.onrender.com/api/v1/app/report-version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ version })
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const checkVersion = async () => {
      // Only run on native mobile (iOS / Android via Capacitor)
      const platform = window.Capacitor?.getPlatform?.() || 'web';
      if (platform === 'web') {
        setUpdateState({ required: false });
        return;
      }

      try {
        const res = await fetch(
          'https://schooner-backend.onrender.com/api/v1/app/config',
          { cache: 'no-store', signal: controller.signal }
        );
        if (!res.ok) throw new Error('non-200');
        const config = await res.json();

        if (platform === 'ios') {
          const minVer = config.minVersions?.ios;
          const currentVer = APP_VERSION.ios;
          if (minVer && isOutdated(currentVer, minVer)) {
            setUpdateState({
              required: true,
              platform: 'ios',
              storeUrl: config.storeUrls?.ios,
              currentVersion: currentVer,
              requiredVersion: minVer,
            });
            return;
          }
        } else if (platform === 'android') {
          const minBuild = config.minVersions?.androidBuildNumber;
          const currentBuild = APP_VERSION.androidBuildNumber;
          const minVer = config.minVersions?.android;
          const currentVer = APP_VERSION.android;
          const outdated =
            (minBuild != null && currentBuild < minBuild) ||
            (minVer && isOutdated(currentVer, minVer));
          if (outdated) {
            setUpdateState({
              required: true,
              platform: 'android',
              storeUrl: config.storeUrls?.android,
              currentVersion: currentVer,
              requiredVersion: minVer,
            });
            return;
          }
        }

        setUpdateState({ required: false });
      } catch {
        // If the check fails (offline, etc.) let the user through — graceful degradation
        setUpdateState({ required: false });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkVersion();
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  if (showUpdatePreview) {
    return <ForceUpdateScreen storeUrl="#" />;
  }

  // While the version check is running, show white (matches ForceUpdateScreen)
  if (updateState === null) {
    return <Box sx={{ position: 'fixed', inset: 0, backgroundColor: '#fff' }} />;
  }

  // Block the entire app with the force-update screen
  if (updateState.required) {
    return (
      <ForceUpdateScreen
        platform={updateState.platform}
        storeUrl={updateState.storeUrl}
        currentVersion={updateState.currentVersion}
        requiredVersion={updateState.requiredVersion}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <Box sx={{ minHeight: '100vh' }}>
              <Suspense fallback={(
                <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}>
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/register/:token" element={<InvitedRegistration />} />

                <Route
                  path="/time-clock"
                  element={
                    <ProtectedRoute requiredRole="employee">
                      <TimeClock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute requiredRole={['manager', 'reports_viewer']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Root path now auto-redirects */}
                <Route path="/" element={<RootRedirect />} />
                </Routes>
              </Suspense>
            </Box>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
